import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MapPin,
  Building,
  Route,
  Tags,
  MessageSquare,
  GitPullRequest,
  ShieldCheck,
  Settings,
  LogOut,
  X,
  Image as ImageIcon,
  FileText,
  Calendar,
  Menu as MenuIcon,
  Flame,
  BookOpen,
  Upload,
} from
'lucide-react';
import {
  clearAdminSession,
  AHG_ADMIN_PROFILE_SESSION,
} from '../../lib/api';
interface AdminSidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
const NAV_GROUPS = [
{
  label: 'Content',
  items: [
  {
    icon: LayoutDashboard,
    label: 'Overview',
    to: '/admin',
    end: true
  },
  {
    icon: MapPin,
    label: 'Places',
    to: '/admin/places'
  },
  {
    icon: Upload,
    label: 'Bulk Import',
    to: '/admin/import'
  },
  {
    icon: Flame,
    label: 'Street Food',
    to: '/admin/street-food'
  },
  {
    icon: Building,
    label: 'Neighborhoods',
    to: '/admin/neighborhoods'
  },
  {
    icon: Route,
    label: 'Itineraries',
    to: '/admin/itineraries'
  },
  {
    icon: BookOpen,
    label: 'Editorial',
    to: '/admin/editorial'
  },
  {
    icon: Tags,
    label: 'Categories',
    to: '/admin/categories'
  },
  {
    icon: ImageIcon,
    label: 'Hero Images',
    to: '/admin/hero-images'
  },
  {
    icon: FileText,
    label: 'Static Pages',
    to: '/admin/static-pages'
  },
  {
    icon: MenuIcon,
    label: 'Menus',
    to: '/admin/menus'
  },
  {
    icon: Calendar,
    label: 'Events',
    to: '/admin/events'
  }]

},
{
  label: 'Moderation',
  items: [
  {
    icon: MessageSquare,
    label: 'Reviews',
    to: '/admin/reviews'
  },
  {
    icon: GitPullRequest,
    label: 'Corrections',
    to: '/admin/corrections'
  },
  {
    icon: ShieldCheck,
    label: 'Verification',
    to: '/admin/verification'
  }]

},
{
  label: 'System',
  items: [
  {
    icon: Settings,
    label: 'Settings',
    to: '/admin/settings'
  }]

}];

/** Sidebar profile from session (set at admin login). */
function readAdminProfile(): {
  displayName: string;
  role: string;
} {
  try {
    const raw =
      typeof window !== 'undefined' ?
        sessionStorage.getItem(AHG_ADMIN_PROFILE_SESSION)
      : null;
    if (!raw)
      return {
        displayName: 'Admin',
        role: 'Editor',
      };
    const parsed = JSON.parse(raw) as {
      displayName?: string;
      role?: string;
    };
    return {
      displayName: parsed?.displayName || 'Admin',
      role: parsed?.role || 'Editor',
    };
  } catch {
    return {
      displayName: 'Admin',
      role: 'Editor',
    };
  }
}
export function AdminSidebar({ isOpen, setIsOpen }: AdminSidebarProps) {
  const navigate = useNavigate();
  const { displayName, role } = readAdminProfile();
  const initials =
  displayName.
  split(' ').
  map((p) => p[0]).
  filter(Boolean).
  slice(0, 2).
  join('').
  toUpperCase() || 'AD';
  const handleSignOut = () => {
    clearAdminSession();
    navigate('/admin/login');
  };
  return (
    <>
      {/* Mobile overlay */}
      {isOpen &&
      <div
        className="fixed inset-0 bg-sea-blue/50 z-40 md:hidden"
        onClick={() => setIsOpen(false)} />

      }

      {/* Sidebar */}
      <aside
        className={`
        fixed md:sticky top-0 left-0 z-50 h-screen w-[260px] bg-sea-blue text-sand flex flex-col
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        
        {/* Header — beige block for brand integrity */}
        <div className="bg-sand shrink-0 relative">
          <div className="h-32 flex items-center justify-between px-4">
            <Link
              to="/admin"
              className="flex items-center group flex-grow justify-center"
              aria-label="Alex Hidden Gems admin home">
              
              <img
                src="/3.svg"
                alt="Alex Hidden Gems"
                className="h-20 w-auto transition-transform group-hover:scale-[1.02]" />
              
            </Link>
            <button
              className="md:hidden absolute top-3 right-3 text-sea-blue/80 hover:text-sea-blue"
              onClick={() => setIsOpen(false)}>
              
              <X size={20} />
            </button>
          </div>
          {/* Subtle bottom accent strip */}
          <div className="h-0.5 bg-gradient-to-r from-cyan-500/40 via-cyan-400 to-cyan-500/40"></div>
        </div>

        {/* Navigation */}
        <div className="flex-grow overflow-y-auto py-6 px-4 space-y-8 hide-scrollbar">
          {NAV_GROUPS.map((group, idx) =>
          <div key={idx}>
              <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3 px-3">
                {group.label}
              </div>
              <nav className="space-y-1">
                {group.items.map((item) =>
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive ? 'bg-white/10 text-white border-l-2 border-terracotta' : 'text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent'}
                    `}>
                
                    {({ isActive }) =>
                <>
                        <item.icon
                    size={18}
                    className={
                    isActive ? 'text-terracotta' : 'opacity-70'
                    } />
                  
                        {item.label}
                      </>
                }
                  </NavLink>
              )}
              </nav>
            </div>
          )}
        </div>

        {/* User Profile Footer */}
        <div className="p-4 border-t border-white/10 shrink-0">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 rounded-full bg-terracotta flex items-center justify-center text-white font-serif font-bold text-sm shrink-0">
              {initials}
            </div>
            <div className="flex flex-col overflow-hidden">
              <span className="text-sm font-bold text-white truncate">
                {displayName}
              </span>
              <span className="text-xs text-cyan-400 font-medium">{role}</span>
            </div>
          </div>
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white px-2 py-2 rounded-lg hover:bg-white/5 transition-colors"
            onClick={handleSignOut}>
            
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>
    </>);

}